import { ICoupon, IProgram, IService } from "@interfaces";
import React, { memo, useEffect, useState } from "react";
import * as Yup from "yup";
import { Input, Form, Row, Col, FormInstance, AutoComplete } from "antd";
import { useYup } from "@validations";
import { useTranslation } from "react-i18next";

import { FormAction } from "@components";
import { PATH_COUPON_LIST } from "@configs";
import { setLoading } from "@redux";
import {
  getApiErrorMessage,
  useCreateCouponFamily,
  useNotify,
  useSearchPrograms,
  useSearchServices,
} from "@utils";
import { useHistory } from "react-router";
import { useDispatch } from "react-redux";

interface IProps {
  coupon?: ICoupon;
}

const initValue: Partial<ICoupon> = {
  programName: "",
  serviceName: "",
  noOfCoupons: 0,
  programId: null,
  serviceId: null,
  familyName: "",
};

export const CouponForm = memo(({ coupon }: IProps) => {
  const formRef = React.useRef<FormInstance>(null);
  const { YupCoupon } = useYup();
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const history = useHistory();
  const { success, error } = useNotify();
  const createCouponFamily = useCreateCouponFamily();
  const couponSchema = Yup.object().shape(YupCoupon);
  const [couponForm] = Form.useForm();
  const [programId, setProgramId] = useState<string>("");
  const [serviceId, setServiceId] = useState<string>("");

  const programVal: string = Form.useWatch("programName", couponForm);
  const serviceVal: string = Form.useWatch("serviceName", couponForm);
  const { data: programResults = [], isFetching: fetchingPrograms } = useSearchPrograms(programVal);
  const { data: serviceResults = [], isFetching: fetchingServices } = useSearchServices(serviceVal);

  useEffect(() => {
    dispatch(setLoading(createCouponFamily.isPending));
  }, [dispatch, createCouponFamily.isPending]);

  const getServiceValues = (data: IService[]) =>
    data.reduce<IService[]>((acc, item) => {
      acc.push({ ...item, value: item?.serviceName || "" });
      return acc;
    }, []);

  const getProgramValues = (data: IProgram[]) =>
    data.reduce<IProgram[]>((acc, item) => {
      acc.push({ ...item, value: item?.programName || "" });
      return acc;
    }, []);

  const onFinish = async (values: ICoupon) => {
    try {
      await createCouponFamily.mutateAsync({
        ...values,
        programId,
        serviceId,
        status: true,
        noOfCoupons: Number(values.noOfCoupons),
      } as ICoupon);
      success(t("message.create.success"));
      history.push(PATH_COUPON_LIST);
    } catch (err) {
      error(getApiErrorMessage(err, t("message.create.fail")));
    }
  };

  const onFinishFailed = (errorInfo: any) => {
    console.log("Failed:", errorInfo);
  };

  const yupSync = {
    async validator({ field }: any, value: any) {
      await couponSchema.validateSyncAt(field, { [field]: value });
    },
  };

  const onSelectProgram = (_value: string, option: IProgram) => {
    setProgramId(option?._id || "");
  };
  const onSelectService = (_value: string, option: IService) => {
    setServiceId(option?._id || "");
  };

  const handleSearchProgram = (value: string) => {
    couponForm.setFieldsValue({ programName: value });
  };
  const handleSearchService = (value: string) => {
    couponForm.setFieldsValue({ serviceName: value });
  };

  return (
    <Form
      form={couponForm}
      name="basic"
      ref={formRef}
      initialValues={initValue}
      onFinish={onFinish}
      onFinishFailed={onFinishFailed}
      layout="vertical"
    >
      <Row gutter={{ md: 54, lg: 54 }}>
        <Col lg={12} md={24} sm={24} xs={24}>
          <Form.Item label={t("page.coupon_family_name")} name="familyName" rules={[yupSync]}>
            <Input />
          </Form.Item>
        </Col>
        <Col lg={12} md={24} sm={24} xs={24}>
          <Form.Item label={t("page.program_name")} name="programName" rules={[yupSync]}>
            <AutoComplete
              options={getProgramValues(programResults)}
              onSelect={onSelectProgram}
              onSearch={handleSearchProgram}
              placeholder="type program name"
              notFoundContent={fetchingPrograms ? "Loading..." : null}
            />
          </Form.Item>
        </Col>
      </Row>
      <Row gutter={{ md: 54, lg: 54 }}>
        <Col lg={12} md={24} sm={24} xs={24}>
          <Form.Item label={t("page.service_name")} name="serviceName" rules={[yupSync]}>
            <AutoComplete
              options={getServiceValues(serviceResults)}
              onSelect={onSelectService}
              onSearch={handleSearchService}
              placeholder="type service name"
              notFoundContent={fetchingServices ? "Loading..." : null}
            />
          </Form.Item>
        </Col>
        <Col lg={12} md={24} sm={24} xs={24}>
          <Form.Item label={t("page.number_of_coupons")} name="noOfCoupons" rules={[yupSync]}>
            <Input type="number" />
          </Form.Item>
        </Col>
      </Row>
      <FormAction />
    </Form>
  );
});
